import { useEffect, useRef } from 'react';

/**
 * @file Subscribes to the engine's `'hud:blips'` channel and parks the result
 * in a REF, not in state.
 *
 * This is the deliberate opposite of `useEngineBus`. That hook exists for
 * values that become DOM, and it re-renders (throttled) when they change. Blips
 * never become DOM — they become pixels inside a canvas that React does not
 * own — so re-rendering for them would be pure waste eight times a second.
 * Consumers here paint from their own rAF loop and read `feed.current`.
 *
 * ⚠ THE PAYLOAD IS REUSED. `engine/systems/Gameplay.js` emits the same object,
 * the same array and the same 64 entry objects every time; retaining any of
 * them means watching them mutate. This hook therefore COPIES into flat arrays
 * inside the handler and keeps no reference to the payload. Anything else that
 * subscribes to `'hud:blips'` must do the same.
 *
 * ⚠ THE MARKER ARRAY IS REUSED TWICE OVER — it is `Missions.js`'s own live
 * array, handed straight through. So the markers are copied into FRESH plain
 * objects here rather than aliased, and the copies are recycled between events
 * so the 8 Hz channel still allocates nothing after the first few frames.
 *
 * @module ui/hooks/useBlipFeed
 */

/**
 * @typedef {Object} BlipFeed
 * @property {Float32Array} x World X per blip, `count` valid entries.
 * @property {Float32Array} z World Z per blip.
 * @property {string[]} kind `'police'|'gang_red'|'gang_green'|'gang_purple'|'civilian'|'car'`.
 * @property {number} count Valid entries in the three arrays above.
 * @property {Array<{x: number, z: number, r: number, kind: string, tracked: boolean,
 *   label: string, id: string, dim: boolean}>} markers Mission markers, COPIED.
 *   `markerCount` valid entries — the array itself is over-long and recycled.
 * @property {number} markerCount
 * @property {{x: number, z: number, yaw: number, driving: boolean, stamp: number}} player
 *   Last reported player position + map heading. `stamp` is `Date.now()` of the
 *   last event — 0 means the channel has never fired. Compare it against
 *   {@link STALE_MS} to decide whether the feed is live.
 *   ⚠ `driving` is `true` while the player is in a car. It rides this channel
 *   because it is a STATE that goes with the position (see the note on
 *   `blipPayload` in `engine/systems/Gameplay.js`), and it DEFAULTS TO FALSE on
 *   every path — a dev scene with no gameplay, a level whose payload predates
 *   the field, and the reset below — so nothing gated on it can be switched on
 *   by a missing value.
 */

/** A feed older than this is stale: no gameplay installed, or the loop is paused. */
export const STALE_MS = 900;

/** Must match `BLIPS.max` in `engine/systems/Gameplay.js`. */
export const MAX_BLIPS = 64;

/** Must match `MISSIONS.worldMarkerMax`'s ceiling — the map gets ALL of them,
 *  and `Missions.js`'s own pool is 32. */
export const MAX_MARKERS = 32;

/**
 * @param {{bus?: import('../../engine/types.js').EventBus}|null|undefined} engine
 * @returns {{current: BlipFeed}} Stable across renders; never null.
 */
export function useBlipFeed(engine) {
  /** @type {{current: BlipFeed|null}} */
  const ref = useRef(null);
  if (ref.current === null) {
    ref.current = {
      x: new Float32Array(MAX_BLIPS),
      z: new Float32Array(MAX_BLIPS),
      kind: new Array(MAX_BLIPS).fill('civilian'),
      count: 0,
      markers: Array.from({ length: MAX_MARKERS }, () => ({
        x: 0, z: 0, r: 0, kind: 'objective', tracked: false, label: '', id: '', dim: false,
      })),
      markerCount: 0,
      player: { x: 0, z: 0, yaw: 0, driving: false, stamp: 0 },
    };
  }

  useEffect(() => {
    const feed = /** @type {BlipFeed} */ (ref.current);
    // A new engine means the old world is gone: drop the stale snapshot rather
    // than letting the previous level's blips flash onto the new one's map.
    feed.count = 0;
    feed.markerCount = 0;
    feed.player.stamp = 0;
    // ...and the player is on foot until the new world says otherwise. A stale
    // `true` here would have the mission arrow up over a dev scene.
    feed.player.driving = false;

    const bus = engine?.bus;
    if (!bus?.on) return undefined;

    const off = bus.on('hud:blips', (payload) => {
      const p = payload?.player;
      if (p) {
        feed.player.x = p.x ?? 0;
        feed.player.z = p.z ?? 0;
        feed.player.yaw = p.yaw ?? 0;
        feed.player.driving = !!p.driving;
        feed.player.stamp = Date.now();
      }
      const list = payload?.blips;
      const n = Math.min(MAX_BLIPS, Array.isArray(list) ? list.length : 0);
      for (let i = 0; i < n; i++) {
        const b = list[i];
        feed.x[i] = b?.x ?? 0;
        feed.z[i] = b?.z ?? 0;
        feed.kind[i] = b?.kind ?? 'civilian';
      }
      feed.count = n;

      // Markers: COPIED field by field into our own recycled records. Aliasing
      // the director's entries would have the map watching them mutate mid-draw.
      const mks = payload?.markers;
      const mn = Math.min(MAX_MARKERS, Array.isArray(mks) ? mks.length : 0);
      for (let i = 0; i < mn; i++) {
        const s = mks[i];
        const d = feed.markers[i];
        d.x = s?.x ?? 0;
        d.z = s?.z ?? 0;
        d.r = s?.r ?? 0;
        d.kind = s?.kind ?? 'objective';
        d.tracked = !!s?.tracked;
        d.label = s?.label ?? '';
        d.id = s?.id ?? '';
        d.dim = !!s?.dim;
      }
      feed.markerCount = mn;
    });

    return () => off?.();
  }, [engine]);

  return /** @type {{current: BlipFeed}} */ (ref);
}

/**
 * Where to draw the player, and which way it is pointing, whether or not the
 * blip channel is alive. Falls back to the camera focus (`engine.target`) and
 * the camera's own forward — which is exactly right for a dev scene, a
 * fly-through, and the paused game (the loop is frozen, so no blip arrives).
 *
 * @param {any} engine
 * @param {BlipFeed} feed
 * @param {{x: number, z: number, yaw: number, live: boolean}} out Reused.
 * @returns {{x: number, z: number, yaw: number, live: boolean}} `out`.
 */
export function resolvePlayerMark(engine, feed, out) {
  const live = Date.now() - feed.player.stamp < STALE_MS;
  if (live) {
    out.x = feed.player.x;
    out.z = feed.player.z;
    out.yaw = feed.player.yaw;
  } else {
    const t = engine?.target;
    out.x = t?.x ?? 0;
    out.z = t?.z ?? 0;
    // The camera orbits to `target + (sin yaw, _, cos yaw) * d`, so it LOOKS
    // along `-(sin, cos)` — heading `yaw + PI` in the engine's `atan2(dx, dz)`.
    out.yaw = (engine?.yaw ?? 0) + Math.PI;
  }
  out.live = live;
  return out;
}
